type ManageEventDraftSummary = {
  id: string;
  eventId?: string | null;
  updatedAtUtcMilli?: number | null;
};

type ManageSavedEvent = { id: string; updatedAtUtcMilli?: number | null };

/**
 * One row per event. A saved event with a draft keeps its own row and opens
 * the newest draft on Edit. A draft saved before the event's last update
 * cannot be published (the event revision changed), so it is not opened.
 * Drafts of unsaved new events keep their own rows. A draft whose event is
 * not in the current result (for example, filtered by status) shows once, as
 * its newest draft. No draft is deleted.
 */
export const groupManageEventDrafts = <
  Event extends ManageSavedEvent,
  Draft extends ManageEventDraftSummary,
>({
  drafts,
  events,
}: {
  drafts: ReadonlyArray<Draft>;
  events: ReadonlyArray<Event>;
}): {
  draftRows: Draft[];
  savedRows: Array<Event & { workingDraftId?: string }>;
} => {
  const newestDraftByEventId = new Map<string, Draft>();
  drafts.forEach((draft) => {
    if (!draft.eventId) {
      return;
    }
    const current = newestDraftByEventId.get(draft.eventId);
    if (!current || (draft.updatedAtUtcMilli || 0) > (current.updatedAtUtcMilli || 0)) {
      newestDraftByEventId.set(draft.eventId, draft);
    }
  });
  const savedEventIds = new Set(events.map((event) => event.id));

  return {
    draftRows: drafts.filter((draft) =>
      !draft.eventId ||
      (!savedEventIds.has(draft.eventId) &&
        newestDraftByEventId.get(draft.eventId) === draft)),
    savedRows: events.map((event) => {
      const workingDraft = newestDraftByEventId.get(event.id);
      const current = workingDraft &&
        (!event.updatedAtUtcMilli ||
          (workingDraft.updatedAtUtcMilli || 0) >= event.updatedAtUtcMilli);
      return workingDraft && current
        ? { ...event, workingDraftId: workingDraft.id }
        : event;
    }),
  };
};
